/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import reduce from 'lodash/reduce';
import toLower from 'lodash/toLower';
import includes from 'lodash/includes';
import size from 'lodash/size';
import keys from 'lodash/keys';
import set from 'lodash/set';
import forEach from 'lodash/forEach';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { type CalendarDate, isSameMonth, startOfMonth } from '@internationalized/date';
import {
    calendarDateToUtcString,
    localToday,
    utcStringToCalendarDate,
} from 'sarsaparilla';
import {
    sortReservedToTop,
    getSortedDataByAvailability,
    getSortedDataBySiteAndLoop,
} from './sort_utils';
import { CAMPSITE_RESERVED_AVAILABLE } from '../constants/availability';
import type {
    CampsiteAvailabilities,
    LocalCampsite,
    Reservation,
    ReservationNight,
    SearchCampsite,
} from '../../../types/camping';
import {
    formatAvailabilityAccessor,
    getCalendarDatesFromRange,
    UTCStringToCalendarDate,
} from './dateUtils';

/**
 * Combine new payload's availability data with possible existing availability data in store from
 * its previous or next month, this serves to cache any month's availability if that month still has
 * dates being shown in the UI
 * @param {object} availabilitiesByMonth - the existing availabilities map in store keyed by month
 * @param {object} newAvailabilitiesByMonth - the newly fetched availabilities map keyed by month
 */
export function consolidateCampgroundAvailability(
    availabilitiesByMonth: CampsiteAvailabilities,
    newAvailabilitiesByMonth: CampsiteAvailabilities
) {
    const newPayload: CampsiteAvailabilities = {};
    /* we cache max 2 months of availability data in store (because the grid displays max 14 days,
    so it only needs max 2 months of data), so if newAvailabilitiesByMonth has 2 months of availability
    data, directly return */
    if (size(newAvailabilitiesByMonth) === 2) {
        return newAvailabilitiesByMonth;
    }
    /* if only 1 month of new availability data has been fetched, combine it with adjacent
    month's cached availability data in store; ignore availability data for non-adjacent months */
    if (size(newAvailabilitiesByMonth) === 1) {
        const newIsoDate = keys(newAvailabilitiesByMonth)[0];

        if (newIsoDate) {
            newPayload[newIsoDate] = newAvailabilitiesByMonth[newIsoDate];

            const prevMonth = calendarDateToUtcString(
                utcStringToCalendarDate(newIsoDate)?.subtract({ months: 1 })
            );
            const nextMonth = calendarDateToUtcString(
                utcStringToCalendarDate(newIsoDate)?.add({ months: 1 })
            );

            if (prevMonth && availabilitiesByMonth[prevMonth]) {
                newPayload[prevMonth] = availabilitiesByMonth[prevMonth];
            } else if (nextMonth && availabilitiesByMonth[nextMonth]) {
                newPayload[nextMonth] = availabilitiesByMonth[nextMonth];
            }
        }
    }
    return newPayload;
}

/**
 * Filter the campsites availabilities object (which contains ALL campsites' availabilities at a campground) by
 * the list of campsites from /search endpoint, so we only end up with an availability map for displayable campsites only
 * Performance: O(n) - length of search campsites
 * @param {object} campsiteAvailabilityMap - combined availability object by campsites
 * @param {array} searchCampsites - array of campsite details from /search filter endpoint
 */
export function filterAvailabilityBySearchCampsites(searchCampsites: SearchCampsite[]) {
    const result: { [key: string]: boolean } = {};

    // if either camping list or search campsite list is empty, return the non-empty one unfiltered
    if (searchCampsites) {
        forEach(searchCampsites, (campsite) => {
            result[campsite.campsite_id] = true;
        });
    }

    return result;
}

/**
 * Filter the campsites availabilities object (which contains ALL campsites' availabilities at a campground) by
 * a filter text string
 * Performance: O(n) - length of campsite availability object
 * @param {object} campsiteAvailabilityMap - combined availability object by campsites
 * @param {array} campsiteIds - existing filtered list of campsite ids
 * @param {string} filterText - filter text
 */
export function filterAvailabilityByFilterText(
    campsites: { [key: string]: SearchCampsite },
    campsiteIds: string[],
    filterText: string
) {
    const processedFilterText = toLower(filterText); // case insensitive

    // return a filtered campsiteAvailabilityMap in which each campsite's site' or 'loop' contain the filter text
    const filteredCampsiteIds: { [key: string]: boolean } = {};
    forEach(campsiteIds, (val, campsiteId) => {
        const campsiteDetails = campsites[campsiteId];

        const { name, loop } = campsiteDetails;
        if (
            includes(toLower(name), processedFilterText) ||
            includes(toLower(loop), processedFilterText)
        ) {
            filteredCampsiteIds[campsiteId] = true;
        }
    });

    return filteredCampsiteIds;
}

/**
 * Sort the campsites availabilities object by provided column/data id and ascending/descending order
 * @param {object} campsiteAvailabilityMap - combined availability object by campsites
 * @param {boolean} isSortAsc - true if order is ascending, false if order is descending
 * @param {string} sortedColumnId - id of the sort column and data id to be sorted by (e.g. 'site', 'loop', or iso date key)
 */
export function sortAvailability({
    campsiteAvailabilityMap,
    isSortAsc,
    sortedColumnId,
    reservation,
    gridStartDate,
}: {
    campsiteAvailabilityMap: Record<string, LocalCampsite>;
    isSortAsc: boolean;
    sortedColumnId: string;
    reservation: Reservation;
    gridStartDate: string;
}) {
    let sortedData;

    const campsiteAvailabilityList = map(
        campsiteAvailabilityMap,
        (campsiteAvailabilities) => campsiteAvailabilities
    );

    // if sorted key is by 'Site' or 'Loop'
    if (includes(['site', 'loop'], sortedColumnId)) {
        sortedData = getSortedDataBySiteAndLoop({
            dataSource: campsiteAvailabilityList,
            isAsc: isSortAsc,
            column: sortedColumnId,
        });
    }
    // else if sorted key is by date
    else {
        sortedData = getSortedDataByAvailability({
            dataSource: campsiteAvailabilityList,
            isAsc: isSortAsc,
            day: sortedColumnId,
            gridStartDate,
        });
    }

    if (reservation) {
        sortedData = sortReservedToTop(cloneDeep(sortedData), reservation);
    }

    // return back an ordered list of campsite id
    return reduce(
        sortedData,
        (result: string[], data: SearchCampsite) => {
            result.push(data.campsite_id);
            return result;
        },
        []
    );
}

export const setAvailabilitiesReservedDates = (
    availabilitiesMap: Record<string, SearchCampsite>,
    reservation: Reservation
) => {
    /* If reservation prop is provided, it means this is a modify. Therefore all dates (except the checkout date)
    with status of "Reserved" that is reserved by the reservation should be marked as "Available" for the purpose of modification */
    if (availabilitiesMap[reservation.campsite_id]) {
        const finalAvailabilities = cloneDeep(availabilitiesMap);

        const {
            start_date: resStartDate,
            end_date: resEndDate,
            night_map: resNightMap,
            campsite_id,
            flags,
        } = reservation;

        // ITINERARY_SINGLE
        if (resNightMap) {
            forEach(resNightMap, (item: ReservationNight, night: string) => {
                const dateKey = formatAvailabilityAccessor(
                    UTCStringToCalendarDate(night)
                );
                set(
                    finalAvailabilities,
                    `${item.campsite_id}.availabilities.${dateKey}`,
                    CAMPSITE_RESERVED_AVAILABLE
                );
            });
            if (flags.boat_included) {
                const dateKey = formatAvailabilityAccessor(
                    UTCStringToCalendarDate(resEndDate)
                );
                set(
                    finalAvailabilities,
                    `${campsite_id}.availabilities.${dateKey}`,
                    CAMPSITE_RESERVED_AVAILABLE
                );
            }
        }
        // STANDARD or ITINERARY
        else {
            const allSelectedDates = getCalendarDatesFromRange(
                UTCStringToCalendarDate(resStartDate),
                UTCStringToCalendarDate(resEndDate)
            );

            forEach(allSelectedDates, (selectedDate) => {
                const dateKey = formatAvailabilityAccessor(selectedDate);
                set(
                    finalAvailabilities,
                    `${reservation.campsite_id}.availabilities.${dateKey}`,
                    CAMPSITE_RESERVED_AVAILABLE
                );
            });
        }

        return finalAvailabilities;
    }

    return availabilitiesMap;
};

/** Earliest grid start. External users cannot look before today; internal users can look back 3 months. */
export function getMinStartDate(isInternal: boolean) {
    return isInternal ? localToday.subtract({ months: 3 }) : localToday;
}

/**
 * A start before the earliest fetchable month never returns availability.
 * Open the grid on that earliest date so availability still loads.
 */
export function startDateOrMin(
    startDate: CalendarDate,
    isInternal: boolean
): CalendarDate {
    const minStartDate = getMinStartDate(isInternal);

    if (startDate.compare(startOfMonth(minStartDate)) < 0) {
        return minStartDate;
    }

    return startDate;
}

export function getNewMonthsToFetch({
    availabilitiesByMonth,
    startDate,
    endDate,
    shouldRefresh = false,
    isInternal,
}: {
    availabilitiesByMonth: object;
    startDate: string;
    endDate: string;
    shouldRefresh: boolean;
    isInternal: boolean;
}) {
    const startDateCalendar = utcStringToCalendarDate(startDate) as CalendarDate;
    const endDateCalendar = utcStringToCalendarDate(endDate) as CalendarDate;
    const sameMonth =
        !!endDateCalendar && isSameMonth(startDateCalendar, endDateCalendar);
    const newMonthsToFetch = [];
    const months = keys(availabilitiesByMonth);

    const minStartDate = getMinStartDate(isInternal);

    if (startDateCalendar.compare(startOfMonth(minStartDate)) < 0) {
        return [];
    }

    // (1) if shouldRefresh flag is TRUE, simply return months between start and end months (inclusively)
    if (shouldRefresh) {
        newMonthsToFetch.push(startDate);

        // if end month is NOT the same as start month, return end month too
        if (!sameMonth) {
            newMonthsToFetch.push(endDate);
        }
    }
    // (2) else, only return months that don't already exist in the availability data in redux
    else {
        if (!includes(months, startDate)) {
            newMonthsToFetch.push(startDate);
        }

        if (
            !sameMonth &&
            !includes(months, calendarDateToUtcString(startOfMonth(endDateCalendar)))
        ) {
            newMonthsToFetch.push(endDate);
        }
    }

    return newMonthsToFetch;
}
